import { DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, HostBinding, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { EnvService } from '@core-mkt/services/env/env.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { SkipNavComponent } from '@core-mkt/shared/components/skip-nav/skip-nav/skip-nav.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBars, faPhone, faSignInAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep } from 'lodash-es';
import { CollapseLinkComponent } from '../collapse-link/collapse-link.component';
import { HeaderNavLinkComponent } from '../header-nav-link/header-nav-link.component';
import type { HeaderNavLink } from './header';
import { headerData, setAccountHeaderData } from './header-data';
import { setMobileHeaderData } from './mobile-header-data';

interface AdaEmbed {
  toggle: () => void;
  setSensitiveMetaFields: (fields: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    adaEmbed?: AdaEmbed;
  }
}

@Component({
  selector: 'ace-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    HeaderNavLinkComponent,
    CollapseLinkComponent,
    FaIconComponent,
    SkipNavComponent,
    LazyImgDirective,
    QueryParamDirective,
  ],
})
export class HeaderComponent implements OnInit {
  @HostBinding('class.sticky-header') headerSticky = false;
  activateMobileMenu = false;
  faHamburger = faBars;
  faTimes = faTimes;
  faPhoneAlt = faPhone;
  faSignInAlt = faSignInAlt;
  headerData: HeaderNavLink[] = headerData;
  accountData: HeaderNavLink[] = [];
  mobileHeaderData: HeaderNavLink[] = [];
  resolverData: PageDataResponse;
  anonID = '';
  showDefaultNav = true;
  showAdaChatbot = false;

  constructor(
    private readonly storageService: StorageService,
    private route: ActivatedRoute,
    private env: EnvService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    if (this.route.snapshot) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData as PageDataResponse);
    }
  }

  get aaLogoBlue(): string {
    return 'https://xgrit-ecom.imgix.net/v3_images/AceableAgent/AA_Logo_Blue.svg?v=1779895920';
  }

  ngOnInit(): void {
    if (this.resolverData?.craftData?.headerData) {
      this.headerSticky = this.resolverData.craftData.headerData.isNavSticky;
    }

    this.showDefaultNav = !this.resolverData?.craftData?.headerData?.hideNav;

    this.anonID = this.storageService.get('ajs_anonymous_id')?.split('"').join('');

    this.accountData = setAccountHeaderData(this.anonID);
    this.mobileHeaderData = setMobileHeaderData(this.anonID);

    const hasAdaChatbotInCraft = !!(
      this.resolverData?.craftData?.pageOptions?.adaChatbot &&
      this.resolverData?.craftData?.pageOptions?.adaChatbot !== ''
    );
    const hasDefaultAda = !!(this.env.get.brandConfig?.defaultAda && this.env.get.brandConfig?.defaultAda !== '');

    this.showAdaChatbot = !hasAdaChatbotInCraft && hasDefaultAda;

    if (this.showAdaChatbot) {
      this.injectHideAdaStyles();
    }
  }

  private injectHideAdaStyles(): void {
    const styleId = 'hide-ada-button';
    if (!this.document.getElementById(styleId)) {
      const style = this.document.createElement('style');
      style.id = styleId;
      style.textContent = `
        iframe#ada-button-frame {
          display: none !important;
        }
      `;
      this.document.head.appendChild(style);
    }
  }

  openAdaChat(): void {
    // Use Ada's global API to toggle the chat
    if (window.adaEmbed) {
      window.adaEmbed.toggle();
    } else {
      // Fallback: try clicking the button
      const adaButton = this.document.getElementById('ada-chat-button') as HTMLButtonElement;
      if (adaButton) {
        adaButton.click();
      }
    }
  }
}
